import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  CardTitle,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  FormText,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import Select from 'react-select';

const AddEditFineMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [classOptions, setClassOptions] = useState([]);

  const [fineData, setFineData] = useState({
    fine_master_id: 0,
    vendor_id: '',
    branch_id: '',
    class_id: null,
    class_name: '',
    is_active: true,
    fine_purpose: '',
    slabs: [{ fine_slab_id: 0, from_days: '', to_days: '', fine_amount: '' }],
  });

  // ✅ Static General option (always on top)
  const GENERAL_OPTION = useMemo(
    () => ({
      value: null,
      label: 'General (applicable to all classes)',
    }),
    []
  );

  // ✅ Edit mode
  useEffect(() => {
    if (location.state) {
      const st = location.state || {};
      setFineData((prev) => ({
        ...prev,
        ...st,
        fine_master_id: st.fine_master_id || st.id || prev.fine_master_id || 0,
        class_id:
          st.class_id === '' || st.class_id === undefined ? null : st.class_id,
        class_name: st.class_name || st.className || prev.class_name || '',
        fine_purpose:
          st.fine_purpose || st.finePurpose || prev.fine_purpose || '',
        is_active: st.is_active ?? prev.is_active ?? true,
        slabs: (st.slabs && st.slabs.length > 0 ? st.slabs : prev.slabs).map(
          (x) => ({
            fine_slab_id: x.fine_slab_id || 0,
            from_days: x.from_days ?? '',
            to_days: x.to_days ?? '',
            fine_amount: x.fine_amount ?? '',
          })
        ),
      }));
    }
  }, [location.state]);

  const canLoadVB = useMemo(() => {
    if (
      isSuperAdmin &&
      (!fineData.vendor_id || Number(fineData.vendor_id) <= 0)
    )
      return false;

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!fineData.branch_id || Number(fineData.branch_id) <= 0)
    )
      return false;

    return true;
  }, [
    fineData.vendor_id,
    fineData.branch_id,
    isSuperAdmin,
    isAdmin,
    hasMultiBranch,
  ]);

  // ✅ Fetch classes + prepend General option
  const fetchClasses = async (searchText = '') => {
    try {
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${
          fineData.vendor_id || ''
        }&branchId=${fineData.branch_id || ''}&searchItem=${encodeURIComponent(
          searchText || ''
        )}`
      );

      if (res?.status === true) {
        const raw = res?.data || [];
        const apiOptions = raw.map((x) => ({
          value: x.id,
          label:
            x.class_Name ?? x.class_display_name ?? x.className ?? 'No Name',
        }));

        // ✅ General always first
        setClassOptions([GENERAL_OPTION, ...apiOptions]);
      } else {
        toast.error(res?.message || 'Failed to load classes');
      }
    } catch (err) {
      console.log(err);
      toast.error('Failed to load classes');
    }
  };

  useEffect(() => {
    if (!canLoadVB) return;
    fetchClasses('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadVB]);

  const onClassSelectChange = (opt) => {
    // ✅ If General selected -> class_id null
    setFineData((prev) => ({
      ...prev,
      class_id: opt?.value ?? null,
      class_name: opt?.label ?? '',
    }));
    setErrorMessage((p) => ({ ...p, class_id: '' }));
  };

  const onChangeHandle = (e) => {
    const { name, value, type, checked } = e.target;
    setFineData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateSlab = (idx, field, value) => {
    setFineData((prev) => {
      const slabs = [...(prev.slabs || [])];
      slabs[idx] = { ...slabs[idx], [field]: value };
      return { ...prev, slabs };
    });
  };

  const addSlabRow = () => {
    setFineData((prev) => ({
      ...prev,
      slabs: [
        ...(prev.slabs || []),
        { fine_slab_id: 0, from_days: '', to_days: '', fine_amount: '' },
      ],
    }));
  };

  const removeSlabRow = (idx) => {
    setFineData((prev) => {
      const slabs = [...(prev.slabs || [])];
      slabs.splice(idx, 1);
      return { ...prev, slabs: slabs.length ? slabs : prev.slabs };
    });
  };

  const validateClient = () => {
    setErrorMessage({});

    if (
      isSuperAdmin &&
      (!fineData.vendor_id || Number(fineData.vendor_id) <= 0)
    ) {
      toast.error('Please select Vendor');
      return false;
    }

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!fineData.branch_id || Number(fineData.branch_id) <= 0)
    ) {
      toast.error('Please select Branch');
      return false;
    }

    if (!fineData.slabs || fineData.slabs.length === 0) {
      toast.error('Please add at least one slab');
      return false;
    }

    for (let i = 0; i < fineData.slabs.length; i++) {
      const s = fineData.slabs[i];
      const from = Number(s.from_days);
      const to = Number(s.to_days);
      const amt = Number(s.fine_amount);

      if (Number.isNaN(from) || Number.isNaN(to) || Number.isNaN(amt)) {
        toast.error(`Invalid slab values at row ${i + 1}`);
        return false;
      }
      if (from < 0 || to < 0) {
        toast.error(`Days cannot be negative (row ${i + 1})`);
        return false;
      }
      if (from > to) {
        toast.error(`From Days cannot be greater than To Days (row ${i + 1})`);
        return false;
      }
      if (amt < 0) {
        toast.error(`Fine amount cannot be negative (row ${i + 1})`);
        return false;
      }
    }

    const ordered = [...fineData.slabs]
      .map((x) => ({ from: Number(x.from_days), to: Number(x.to_days) }))
      .sort((a, b) => a.from - b.from || a.to - b.to);

    for (let i = 0; i < ordered.length - 1; i++) {
      if (ordered[i].to >= ordered[i + 1].from) {
        toast.error('Slab ranges are overlapping. Please correct slabs.');
        return false;
      }
    }

    return true;
  };

  const SaveFineMaster = async () => {
    if (!validateClient()) return;

    try {
      const vendorId = Number(fineData.vendor_id || 0);
      const branchId = Number(fineData.branch_id || 0);

      const payload = {
        fine_master_id:
          fineData.fine_master_id && Number(fineData.fine_master_id) > 0
            ? Number(fineData.fine_master_id)
            : null,

        vendor_id: vendorId > 0 ? vendorId : null,
        branch_id: branchId > 0 ? branchId : null,

        class_id:
          fineData.class_id === null ||
          fineData.class_id === '' ||
          fineData.class_id === undefined
            ? null
            : Number(fineData.class_id),

        class_name: fineData.class_name || null,
        fine_purpose: fineData.fine_purpose || null,
        is_active: !!fineData.is_active,

        slabs: (fineData.slabs || []).map((s) => ({
          fine_slab_id:
            s.fine_slab_id && Number(s.fine_slab_id) > 0
              ? Number(s.fine_slab_id)
              : null,
          from_days: Number(s.from_days),
          to_days: Number(s.to_days),
          fine_amount: Number(s.fine_amount),
          vendor_id: vendorId > 0 ? vendorId : null,
          branch_id: branchId > 0 ? branchId : null,
        })),
      };

      let res;
      if (fineData.fine_master_id && Number(fineData.fine_master_id) > 0) {
        res = await apiService.put(
          `api/FineMaster/UpdateFineMaster?id=${Number(
            fineData.fine_master_id
          )}`,
          payload
        );
      } else {
        res = await apiService.post(`api/FineMaster/AddFineMaster`, payload);
      }

      if (res?.status === true) {
        toast.success(res.message || 'Saved');
        navigate('/list-fine-master');
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  // ✅ Selected option for react-select
  const selectedClassOption = useMemo(() => {
    if (
      fineData.class_id === null ||
      fineData.class_id === '' ||
      fineData.class_id === undefined
    ) {
      // if class_id null, show General option (if class_name not blank)
      return fineData.class_name ? GENERAL_OPTION : null;
    }

    return (
      classOptions.find((x) => String(x.value) === String(fineData.class_id)) ||
      null
    );
  }, [fineData.class_id, fineData.class_name, classOptions, GENERAL_OPTION]);

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={fineData}
        setSelectionData={setFineData}
        accessButton={true}
        errorMessage={errorMessage}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='border-bottom p-3 d-flex justify-content-between align-items-center'>
          <span>Fine Master Form</span>
          <Button color='warning' onClick={() => navigate('/list-fine-master')}>
            <i className='bi bi-arrow-left' />
          </Button>
        </CardTitle>

        <CardBody>
          <Row>
            <FormGroup className='col-sm-6'>
              <Label>Class</Label>
              <Select
                options={classOptions} // ✅ includes General option
                value={selectedClassOption}
                onChange={onClassSelectChange}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    fetchClasses(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Search class...'
              />
              <FormText color='danger'>{errorMessage.class_id}</FormText>
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Fine Purpose</Label>
              <Input
                type='text'
                name='fine_purpose'
                value={fineData.fine_purpose || ''}
                onChange={onChangeHandle}
                placeholder='e.g. Library fine / Late fee / Discipline fine'
              />
            </FormGroup>
          </Row>

          <Row className='mt-2'>
            <FormGroup className='col-sm-6'>
              <Label check>
                <Input
                  type='checkbox'
                  name='is_active'
                  checked={!!fineData.is_active}
                  onChange={onChangeHandle}
                />{' '}
                Active
              </Label>
            </FormGroup>
          </Row>

          <div className='d-flex justify-content-between align-items-center mt-3'>
            <h6 className='mb-0'>Fine Slabs</h6>
            <Button color='secondary' size='sm' onClick={addSlabRow}>
              <i className='bi bi-plus-circle' /> Add Slab
            </Button>
          </div>

          <Table striped hover responsive className='mt-2'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>From Days</th>
                <th>To Days</th>
                <th>Fine Amount</th>
                <th style={{ width: 80 }}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {(fineData.slabs || []).map((s, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <Input
                      type='number'
                      value={s.from_days}
                      onChange={(e) =>
                        updateSlab(idx, 'from_days', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Input
                      type='number'
                      value={s.to_days}
                      onChange={(e) =>
                        updateSlab(idx, 'to_days', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Input
                      type='number'
                      step='0.01'
                      value={s.fine_amount}
                      onChange={(e) =>
                        updateSlab(idx, 'fine_amount', e.target.value)
                      }
                    />
                  </td>
                  <td className='text-center'>
                    <i
                      className='bi bi-trash text-danger'
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeSlabRow(idx)}
                      title='Remove'
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button
            color='primary'
            className='float-end mt-3'
            onClick={SaveFineMaster}
          >
            Save
          </Button>
        </CardBody>
      </Card>
    </>
  );
};

export default AddEditFineMaster;
